import { apiClient } from '@/shared/api';

import type { InventoryLocationSummary } from '../model/types';

/**
 * Minimal fulfilment-location lookup for inventory screens (id → name enrichment).
 *
 * web-inventory-service only returns opaque `locationId` UUIDs — it does not own location names
 * (deliberate bounded-context separation). Location names ARE cheaply resolvable, though: this
 * BFF already exposes `POST /api/v1/store/locations/search` (used by
 * `store-settings/api/storeServiceApi.ts`'s `searchStoreLocations`). FSD forbids importing across
 * features, so this is a small, local equivalent call rather than a cross-feature import — the
 * request/response shapes below intentionally mirror only the fields this feature needs from the
 * real `StoreLocationSearchRequest`/`StoreLocationPageResponse` schemas.
 */

interface StoreLocationSearchRequestDto {
  activeOnly?: boolean;
  page?: number;
  size?: number;
}

interface StoreLocationResponseDto {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

interface StoreLocationPageResponseDto {
  items: StoreLocationResponseDto[];
}

/** `StoreLocationSearchRequest.size` caps at 100 — matches the BFF-enforced maximum. */
const MAX_SEARCH_SIZE = 100;

/**
 * Fetches up to {@link MAX_SEARCH_SIZE} tenant locations (active and inactive — a ledger entry
 * may reference a location that has since been deactivated) for an id→name lookup.
 *
 * Tenants are expected to have tens, not thousands, of fulfilment locations, so a single page is
 * fetched. A tenant with more than {@link MAX_SEARCH_SIZE} locations would have some entries fall
 * back to showing a raw id instead of a name — a known, accepted limitation rather than a silent
 * one (see the follow-up note in this feature's `index.ts`).
 */
export const searchAllLocations = async (): Promise<InventoryLocationSummary[]> => {
  const body: StoreLocationSearchRequestDto = { size: MAX_SEARCH_SIZE };
  const { data } = await apiClient.post<StoreLocationPageResponseDto>(
    '/api/v1/store/locations/search',
    body,
  );
  return data.items.map((item) => ({
    id: item.id,
    code: item.code,
    name: item.name,
    active: item.active,
  }));
};
